import { Pressable, View, StyleProp, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Body, Caption } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';

type ActiveTripPeekProps = {
  trip: Trip;
  onExpand: (tripId: string) => void;
  onDismiss?: (tripId: string) => void;
  briefingLine?: string | null;
  style?: StyleProp<ViewStyle>;
  variant?: 'inline' | 'floating';
};

export function ActiveTripPeek({
  trip,
  onExpand,
  onDismiss,
  briefingLine,
  style,
  variant = 'inline',
}: ActiveTripPeekProps) {
  const theme = useTheme();

  const baseStyles = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing(1),
    paddingHorizontal: theme.spacing(1.5),
    paddingVertical: theme.spacing(1),
    borderRadius: variant === 'floating' ? theme.radii.pill : theme.radii.md,
    backgroundColor: variant === 'floating' ? theme.colors.surface : theme.colors.borderMuted + '40',
    borderWidth: variant === 'floating' ? 1 : 0,
    borderColor: variant === 'floating' ? theme.colors.border : 'transparent',
  };

  return (
    <Pressable
      onPress={() => onExpand(trip.id)}
      style={({ pressed }) => [
        {
          opacity: pressed ? 0.8 : 1,
        },
        baseStyles,
        style,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`Active trip minimized for ${trip.name}`}
      accessibilityHint="Tap to reopen trip card"
    >
      <Ionicons name="airplane-outline" size={18} color={theme.colors.text} />
      <View style={{ flex: 1 }}>
        <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
          {briefingLine ? "Today's brief" : 'Active trip minimized'}
        </Caption>
        <Body style={{ fontWeight: '600' }} numberOfLines={1}>
          {briefingLine || trip.name}
        </Body>
      </View>
      {onDismiss && (
        <Pressable
          onPress={(event) => {
            event.stopPropagation();
            onDismiss(trip.id);
          }}
          style={{
            padding: theme.spacing(0.5),
            borderRadius: theme.radii.pill,
          }}
          accessibilityRole="button"
          accessibilityLabel="Dismiss active trip card"
        >
          <Ionicons name="close" size={16} color={theme.colors.textSecondary} />
        </Pressable>
      )}
    </Pressable>
  );
}


